<i18n>
{
  "en": {
    "baseline": {
      "text": "Convert csv contact file to VCard {0}{1}",
      "accent": "safely in a blink of an eye 😉"
    },
    "processingpayment": "A moment please, we are processing your payment...",
    "contacts": "no contact was found | 1 contact was found | {nb} contacts were found",
    "convertto": {
      "outlook": "Convert Outlook CSV to VCard",
      "google": "Convert Google (Gmail) CSV to VCard",
      "custom": "Convert Custom CSV contacts file to VCard"
    },
    "cta": {
      "convert": "Start Conversion"
    },
    "messages": {
      "nofile": "You didn't select any file.",
      "badfile": "This is not a standard CSV (.csv) file.",
      "nodata": "We couldn't find any data in your file.",
      "typenotready": "This type of conversion is not ready yet. If you are interested in a custom CSV file conversion, send us an e-mail with your needs at: contact@vcard-converter.me",
      "paymentcancel": "It seems you cancelled your payment... 😥 Can we help in any way? Check the FAQ at the bottom of the page or contact us by e-mail: contact@vcard-converter.me",
      "paymentinvalid": "Something went wrong with the payment... 😥 For all questions contact@vcard-converter.me",
      "paymentdone": "The payment was successful!",
      "parsing": "An error occured during the parsing of your file, send us your file's size and extensio by e-mail so that we can look into that. contact@vcard-converter.me"
    }
  }
}
</i18n>

<script>
import Papa from 'papaparse';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import { csvAcceptedFormats } from '@/config/constants';
import {
  state,
  mapGetters,
  setState,
  downloadContacts,
} from '@/config/store';
import { updateUser } from '@/config/auth';
import { isValidToken, getPlan } from '@/config/payment';
import FileDropzone from '@/components/FileDropzone.vue';
import DownloadPlans from '@/components/DownloadPlans.vue';

export default {
  name: 'Home',
  metaInfo: {
    title: 'VCardConverter',
  },

  components: {
    FileDropzone,
    DownloadPlans,
  },

  data() {
    return {
      file: null,
      type: 'outlook',
      shared: state,
      isLoading: false,
      isDraggedOver: false,
      isCheckingPayment: false,
      csvAcceptedFormats: csvAcceptedFormats.join(', '),
    };
  },

  computed: {
    ...mapGetters(),
  },

  methods: {
    onTypeChange() {
      if (this.type === 'custom') {
        this.type = 'outlook';
        this.$addMessage(this.$t('messages.typenotready'), 'info', 10000);
        this.$trackCategory('UPLOAD', 'want CUSTOM TYPE');
      }
    },
    checkFile(file, save = false) {
      setState('contacts', null);
      this.$trackCategory('UPLOAD', 'newfile');

      if (!file) {
        this.file = null;
        this.$addMessage(this.$t('messages.nofile'), 'danger');
        this.$trackCategory('UPLOAD', 'nofile');
        return false;
      } else if (!csvAcceptedFormats.includes(file.type) && !file.name.endsWith('.csv')) {
        this.file = null;
        this.$addMessage(this.$t('messages.badfile'), 'danger');
        this.$trackCategory('UPLOAD', 'badfile', `${file.name} : ${file.type}`);
        return false;
      }

      if (save) {
        this.file = file;
      }

      return true;
    },
    convertFile() {
      const file = this.file;

      if (!this.checkFile(file)) {
        return;
      }

      this.isLoading = true;

      this.$nextTick(() => {
        this.$trackCategory('CONVERT', 'start', this.type);

        let fullData = [];

        Papa.parse(file, {
          header: true,
          worker: true,
          chunk({ data }) {
            if (!isArray(data)) {
              return;
            }

            fullData = [...fullData, ...data];
          },
          complete: () => {
            const data = fullData;

            this.file = null;
            this.isLoading = false;

            if (data.length <= 0) {
              setState('contacts', null);
              this.$addMessage(this.$t('messages.nodata'), 'danger');
              this.$trackCategory('CONVERT', 'done - nodata');
              return;
            }

            setState('contacts', data);
            this.$trackCategory('CONVERT', 'done - with data', data.length);

            if (this.canDownloadFull) {
              downloadContacts(this.type, false);
              this.$trackCategory('CONVERT', `download - ${this.shared.user.plan}`, this.type);
            }
          },
          error(err) {
            this.$addMessage(this.$t('messages.parsing'), 'danger', 10000);
            this.$trackError(err);
          },
        });
      });
    },

    validPaymentIfPresent() {
      const query = { ...this.$route.query };

      if (isString(query.cancel)) {
        this.$router.replace({ query: {} });
        this.$addMessage(this.$t('messages.paymentcancel'), 'danger', 10000);
        this.$trackCategory('PAYMENT', 'cancel stripe');
      } else if (isString(query.s)) {
        this.$router.replace({ query: {} });

        if (!isValidToken(query.s)) {
          this.$addMessage(this.$t('messages.paymentinvalid'), 'danger', 6000);
          this.$trackCategory('PAYMENT', 'invalid tkn');
          return;
        }

        this.isCheckingPayment = true;
        this.$trackCategory('PAYMENT', 'done - will update user');
        updateUser({ plan: getPlan(), paymentAt: Date.now() })
          .then(() => {
            this.isCheckingPayment = false;
            this.$addMessage(this.$t('messages.paymentdone'), 'success');
            this.$trackCategory('PAYMENT', 'done - user updated');
          })
          .catch((err) => {
            this.isCheckingPayment = false;
            this.$trackCategory('PAYMENT', 'done - user not updated');
            this.$trackError(err);
          });
      }
    },
  },

  created() {
    this.validPaymentIfPresent();
  },
};
</script>

<template>
  <div class="home px-5 mt-10 text-center">
    <teleport to="title">
      VcardConverter
    </teleport>

    <p
      v-if="isCheckingPayment"
      class="mxw-520 mx-auto mb-3 p-5 fsize-5 color-main bgc-light radius"
      v-t="'processingpayment'"
    />

    <template v-else>
      <i18n class="fsize-5 color-black" tag="h2" path="baseline.text">
        <br>
        <strong class="color-main" v-t="'baseline.accent'" />
      </i18n>

      <form class="form w-360 mxw-full mt-10 mx-auto" @submit.prevent="convertFile">
        <p>
          <file-dropzone :text="file && file.name || ''" @change="checkFile($event, true)" />
        </p>
        <p class="mt-3">
          <select class="select w-full" v-model="type" @change="onTypeChange">
            <option value="outlook" v-t="'convertto.outlook'" />
            <option value="google" v-t="'convertto.google'" />
            <option value="custom" v-t="'convertto.custom'" />
          </select>
        </p>
        <p class="mt-3">
          <button type="submit" class="btn w-full" v-t="'cta.convert'" :disabled="isLoading || !file" />
        </p>
        <p v-show="isLoading" class="mt-3 fsize-5 color-main" v-t="'loading'" />
        <p
          v-if="shared.contacts"
          class="mt-3 fsize-5 fw-700 color-success"
          v-t="{path: 'contacts', choice: shared.contacts.length, args: {nb: shared.contacts.length}}"
        />
      </form>
    </template>

    <download-plans v-if="shared.contacts && canOnlyFreeDownload" class="mt-5" :type="type" />
  </div>
</template>
